"use client";

import { useCallback, useState, type ReactNode } from "react";
import type { Editor } from "@tiptap/react";
import { useTranslations } from "next-intl";
import { Bold, CheckSquare, ChevronDown, Code, Italic, Link as LinkIcon, List, ListOrdered, Quote, Strikethrough } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface EditorToolbarProps {
  editor: Editor | null;
  className?: string;
}

export function EditorToolbar({ editor, className }: EditorToolbarProps) {
  const t = useTranslations("Notes");
  const [linkUrl, setLinkUrl] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);

  const currentHeadingLevel = editor
    ? ([1, 2, 3, 4] as const).find((level) => editor.isActive("heading", { level })) ?? 0
    : 0;

  const headingLabel = currentHeadingLevel > 0 ? `H${currentHeadingLevel}` : t("toolbar.text");

  const setLink = useCallback(() => {
    if (!editor) return;
    if (!linkUrl.trim()) {
      editor.chain().focus().unsetLink().run();
      setLinkUrl("");
      setShowLinkInput(false);
      return;
    }

    const href = /^https?:\/\//.test(linkUrl) ? linkUrl : `https://${linkUrl}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkUrl("");
    setShowLinkInput(false);
  }, [editor, linkUrl]);

  const setParagraph = useCallback(() => {
    editor?.chain().focus().setParagraph().run();
  }, [editor]);

  const toggleHeading = useCallback(
    (level: 1 | 2 | 3 | 4) => {
      editor?.chain().focus().toggleHeading({ level }).run();
    },
    [editor]
  );

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-[3px] border-b bg-card p-[7px_10px]",
        className
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-[29px] gap-1 rounded-[7px] px-2 text-[12.5px] font-medium" disabled={!editor}>
            <span className="w-8 text-left">{headingLabel}</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={setParagraph} className={cn(currentHeadingLevel === 0 && "bg-muted")}>
            <span>{t("toolbar.normalText")}</span>
          </DropdownMenuItem>
          {[1, 2, 3, 4].map((level) => (
            <DropdownMenuItem
              key={level}
              onClick={() => toggleHeading(level as 1 | 2 | 3 | 4)}
              className={cn(currentHeadingLevel === level && "bg-muted")}
            >
              <span
                className={cn(
                  level === 1 && "font-bold text-xl",
                  level === 2 && "font-bold text-lg",
                  level === 3 && "font-semibold",
                  level === 4 && "font-medium text-sm"
                )}
              >
                {t(`toolbar.heading${level}`)}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Separator orientation="vertical" className="mx-[5px] h-5" />

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleBold().run()}
        isActive={!!editor?.isActive("bold")}
        title={t("toolbar.bold")}
        disabled={!editor}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleItalic().run()}
        isActive={!!editor?.isActive("italic")}
        title={t("toolbar.italic")}
        disabled={!editor}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleStrike().run()}
        isActive={!!editor?.isActive("strike")}
        title={t("toolbar.strikethrough")}
        disabled={!editor}
      >
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-[5px] h-5" />

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        isActive={!!editor?.isActive("bulletList")}
        title={t("toolbar.bulletList")}
        disabled={!editor}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        isActive={!!editor?.isActive("orderedList")}
        title={t("toolbar.numberedList")}
        disabled={!editor}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleTaskList().run()}
        isActive={!!editor?.isActive("taskList")}
        title={t("toolbar.taskList")}
        disabled={!editor}
      >
        <CheckSquare className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-[5px] h-5" />

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleBlockquote().run()}
        isActive={!!editor?.isActive("blockquote")}
        title={t("toolbar.quote")}
        disabled={!editor}
      >
        <Quote className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
        isActive={!!editor?.isActive("codeBlock")}
        title={t("toolbar.codeBlock")}
        disabled={!editor}
      >
        <Code className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-[5px] h-5" />

      {showLinkInput ? (
        <div className="flex items-center gap-1">
          <input
            type="text"
            placeholder={t("toolbar.enterUrl")}
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                setLink();
              }
              if (event.key === "Escape") {
                setShowLinkInput(false);
                setLinkUrl("");
              }
            }}
            className="h-7 px-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring w-40"
            autoFocus
          />
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={setLink}>
            {t("toolbar.add")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl("");
            }}
          >
            {t("toolbar.cancel")}
          </Button>
        </div>
      ) : (
        <ToolbarButton
          onClick={() => setShowLinkInput(true)}
          isActive={!!editor?.isActive("link")}
          title={t("toolbar.link")}
          disabled={!editor}
        >
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
      )}
    </div>
  );
}

function ToolbarButton({
  onClick,
  isActive,
  title,
  disabled,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  title: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      className={cn(
        "inline-flex h-[29px] w-[29px] items-center justify-center rounded-[7px] transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5",
        isActive && "bg-accent text-primary",
      )}
      onClick={onClick}
      title={title}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  );
}
